import React, { useState, useEffect, useMemo } from 'react';
import {
    Eye, CheckCircle, Truck, Package, XCircle, Clock, RefreshCw,
    MapPin, User, Phone, Mail, Search, ShoppingBag, DollarSign
} from 'lucide-react';
import apiFetch from '../../../../utils/api';
import './AdminEcommerce.css';

const STATUSES = ['Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];

const getStatusIcon = (status) => {
    switch (status) {
        case 'Pending': return <Clock size={14} />;
        case 'Processing': return <Package size={14} />;
        case 'Shipped': return <Truck size={14} />;
        case 'Delivered': return <CheckCircle size={14} />;
        case 'Cancelled': return <XCircle size={14} />;
        default: return null;
    }
};

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [statusFilter, setStatusFilter] = useState('All');
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const response = await apiFetch('/api/admin/orders');
            const data = await response.json();
            setOrders(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (orderId, newStatus) => {
        setStatusUpdating(true);
        try {
            const response = await apiFetch(`/api/admin/orders/${orderId}/status`, {
                method: 'POST',
                body: JSON.stringify({ status: newStatus })
            });
            const result = await response.json();
            if (result.success) {
                setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
                if (selectedOrder && selectedOrder.id === orderId) {
                    setSelectedOrder(prev => ({ ...prev, status: newStatus }));
                }
            }
        } catch (err) {
            alert('Failed to update status');
        } finally {
            setStatusUpdating(false);
        }
    };

    const stats = useMemo(() => {
        const total = orders.length;
        const pending = orders.filter(o => o.status === 'Pending').length;
        const shipped = orders.filter(o => o.status === 'Shipped').length;
        const delivered = orders.filter(o => o.status === 'Delivered').length;
        const revenue = orders
            .filter(o => o.status !== 'Cancelled')
            .reduce((sum, o) => sum + (o.totalAmount || 0) + (o.shippingFee || 0), 0);
        return { total, pending, shipped, delivered, revenue };
    }, [orders]);

    const filteredOrders = useMemo(() => {
        return orders.filter(o => {
            if (statusFilter !== 'All' && o.status !== statusFilter) return false;
            if (search) {
                const q = search.toLowerCase();
                return (`${o.firstName} ${o.lastName}`.toLowerCase().includes(q)) ||
                       (o.email || '').toLowerCase().includes(q) ||
                       (o.phone || '').toLowerCase().includes(q) ||
                       String(o.id).includes(q);
            }
            return true;
        });
    }, [orders, statusFilter, search]);

    return (
        <div className="admin-ecom-page">
            <div className="admin-ecom-header">
                <div>
                    <h1>Customer Orders</h1>
                    <p>Track and manage guest e-commerce orders.</p>
                </div>
                <button className={`admin-refresh-btn ${loading ? 'loading' : ''}`} onClick={fetchOrders} disabled={loading}>
                    <RefreshCw size={18} className={loading ? 'spin' : ''} />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Stats */}
            <div className="admin-ecom-stats">
                <div className="ecom-stat-card">
                    <div className="ecom-stat-icon stat-blue"><ShoppingBag size={20} /></div>
                    <div>
                        <p className="ecom-stat-label">Total Orders</p>
                        <h3>{stats.total}</h3>
                    </div>
                </div>
                <div className="ecom-stat-card">
                    <div className="ecom-stat-icon stat-amber"><Clock size={20} /></div>
                    <div>
                        <p className="ecom-stat-label">Pending</p>
                        <h3>{stats.pending}</h3>
                    </div>
                </div>
                <div className="ecom-stat-card">
                    <div className="ecom-stat-icon stat-purple"><Truck size={20} /></div>
                    <div>
                        <p className="ecom-stat-label">Shipped</p>
                        <h3>{stats.shipped}</h3>
                    </div>
                </div>
                <div className="ecom-stat-card">
                    <div className="ecom-stat-icon stat-green"><DollarSign size={20} /></div>
                    <div>
                        <p className="ecom-stat-label">Revenue</p>
                        <h3>Rs. {stats.revenue.toLocaleString()}</h3>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="admin-ecom-toolbar">
                <div className="admin-search-input">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Search by name, email, phone, or order ID..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="admin-filter-chips">
                    {['All', ...STATUSES].map(s => (
                        <button
                            key={s}
                            className={`admin-chip ${statusFilter === s ? 'active' : ''}`}
                            onClick={() => setStatusFilter(s)}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="admin-ecom-loading">
                    <div className="ec-spinner" />
                    <p>Loading orders...</p>
                </div>
            ) : (
                <div className={`admin-order-container ${selectedOrder ? 'with-panel' : ''}`}>
                    <div className="admin-order-list-panel">
                        {filteredOrders.length === 0 ? (
                            <div className="admin-ecom-empty">
                                <ShoppingBag size={36} />
                                <h3>No orders found</h3>
                                <p>{search || statusFilter !== 'All' ? 'Try adjusting filters.' : 'Orders will appear here once placed.'}</p>
                            </div>
                        ) : (
                            <div className="admin-order-table-wrap">
                                <table className="admin-product-table">
                                    <thead>
                                        <tr>
                                            <th>Order</th>
                                            <th>Customer</th>
                                            <th>City</th>
                                            <th>Items</th>
                                            <th>Total</th>
                                            <th>Status</th>
                                            <th className="actions-col">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOrders.map(o => {
                                            const itemCount = o.items?.reduce((s, i) => s + i.quantity, 0) || 0;
                                            return (
                                                <tr
                                                    key={o.id}
                                                    className={selectedOrder?.id === o.id ? 'selected' : ''}
                                                    onClick={() => setSelectedOrder(o)}
                                                >
                                                    <td>
                                                        <div className="order-id-cell">
                                                            <strong>#{o.id}</strong>
                                                            <span>{o.createdAt}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="admin-order-cust">
                                                            <strong>{o.firstName} {o.lastName}</strong>
                                                            <span>{o.phone}</span>
                                                        </div>
                                                    </td>
                                                    <td>{o.city}</td>
                                                    <td>{itemCount} {itemCount === 1 ? 'item' : 'items'}</td>
                                                    <td className="price-cell">Rs. {(o.totalAmount + (o.shippingFee || 0)).toLocaleString()}</td>
                                                    <td>
                                                        <div className={`admin-order-status-badge ${o.status.toLowerCase()}`}>
                                                            {getStatusIcon(o.status)}
                                                            <span>{o.status}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="admin-view-order-btn"
                                                            onClick={(e) => { e.stopPropagation(); setSelectedOrder(o); }}
                                                        >
                                                            <Eye size={14} /> View
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {selectedOrder && (
                        <aside className="admin-order-detail-panel">
                            <div className="admin-od-header">
                                <div>
                                    <h3>Order #{selectedOrder.id}</h3>
                                    <span className="admin-od-date">{selectedOrder.createdAt}</span>
                                </div>
                                <button className="admin-od-close" onClick={() => setSelectedOrder(null)}><XCircle size={22} /></button>
                            </div>

                            <div className={`admin-od-status-chip ${selectedOrder.status.toLowerCase()}`}>
                                {getStatusIcon(selectedOrder.status)}
                                <span>{selectedOrder.status}</span>
                            </div>

                            <div className="admin-od-section">
                                <h4><MapPin size={14} /> Shipping Details</h4>
                                <div className="admin-od-info-list">
                                    <div className="info-row">
                                        <User size={14} />
                                        <span>Name</span>
                                        <strong>{selectedOrder.firstName} {selectedOrder.lastName}</strong>
                                    </div>
                                    <div className="info-row">
                                        <Mail size={14} />
                                        <span>Email</span>
                                        <strong>{selectedOrder.email}</strong>
                                    </div>
                                    <div className="info-row">
                                        <Phone size={14} />
                                        <span>Phone</span>
                                        <strong>{selectedOrder.phone}</strong>
                                    </div>
                                    <div className="info-row">
                                        <MapPin size={14} />
                                        <span>City</span>
                                        <strong>{selectedOrder.city}{selectedOrder.state ? `, ${selectedOrder.state}` : ''}</strong>
                                    </div>
                                    <div className="info-row address">
                                        <span>Address</span>
                                        <p>
                                            {selectedOrder.addressLine1}{selectedOrder.addressLine2 ? `, ${selectedOrder.addressLine2}` : ''}<br />
                                            {selectedOrder.postalCode} {selectedOrder.country}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="admin-od-section">
                                <h4><Package size={14} /> Order Items</h4>
                                <div className="admin-od-items">
                                    {selectedOrder.items.map((item, idx) => (
                                        <div key={idx} className="admin-od-item">
                                            <div className="item-head">
                                                <div className="item-name">{item.productName}</div>
                                                <div className="item-total">Rs. {(item.quantity * item.price).toLocaleString()}</div>
                                            </div>
                                            <div className="item-meta">{item.quantity} × Rs. {item.price.toLocaleString()}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="admin-od-bottom">
                                    <div className="total-row">
                                        <span>Subtotal</span>
                                        <strong>Rs. {selectedOrder.totalAmount.toLocaleString()}</strong>
                                    </div>
                                    <div className="total-row">
                                        <span>Shipping</span>
                                        <strong>Rs. {(selectedOrder.shippingFee || 0).toLocaleString()}</strong>
                                    </div>
                                    <div className="total-row grand">
                                        <span>Total</span>
                                        <strong>Rs. {(selectedOrder.totalAmount + (selectedOrder.shippingFee || 0)).toLocaleString()}</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="admin-od-section">
                                <h4>Update Status</h4>
                                <div className="admin-status-actions">
                                    {STATUSES.map(status => (
                                        <button
                                            key={status}
                                            disabled={statusUpdating || selectedOrder.status === status}
                                            className={`status-btn ${status.toLowerCase()} ${selectedOrder.status === status ? 'active' : ''}`}
                                            onClick={() => handleUpdateStatus(selectedOrder.id, status)}
                                        >
                                            {getStatusIcon(status)} {status}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminOrders;
